import React, { useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import { orderBy, sortBy } from "lodash";

import { LoadingSlide } from "@components/title-slide-view";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { PREVIEW_LABEL_RULE_MATCHES } from "@gql/label-rules-v3";
import { useNorthbeamLazyQuery } from "@hooks/http";
import {
  type PreviewLabelRuleMatches,
  type PreviewLabelRuleMatchesVariables,
} from "@north-beam/nb-client-side";
import { segmentAsJSON, type SegmentDescriptor } from "@north-beam/nb-common";
import { Paginator } from "@shared/paginator";

import { LabelRuleSimulatorTable } from "../breakdowns/components/edit-breakdown/advanced-editor/segment-modal/label-rule-simulator-table";
import { type Sorting } from "../breakdowns/components/edit-breakdown/advanced-editor/types";
import { SearchInput } from "../breakdowns/components/labels-assignment/search-input";
import { useLabelWizard } from "../label-rule-wizard/hooks";

import { Tabs, type TabId } from "./tabs";

const PER_PAGE = 20;

interface Props {
  segments: SegmentDescriptor[];
  segment: SegmentDescriptor;
}

export const AdLabelMatches = ({ segments, segment }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { labelFilters } = useLabelWizard();

  const [sorting, setSorting] = useState<Sorting>({
    column: "priorityOrderedMatchedRules",
    order: "desc",
  });
  const [activeTab, setActiveTab] = useState<TabId>("campaign");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const {
    loadData: simulate,
    data: { data, loading, error },
  } = useNorthbeamLazyQuery<
    PreviewLabelRuleMatches,
    PreviewLabelRuleMatchesVariables
  >(PREVIEW_LABEL_RULE_MATCHES);

  useEffect(() => {
    const labelRules = [];

    segments.forEach((e) => {
      if (e.id === segment.id) {
        return;
      }

      if (!e.active) {
        return;
      }

      const rule = segmentAsJSON(e, labelFilters);

      labelRules.push({
        active: true,
        createdAt: "2000-01-01T00:00:00Z",
        name: rule.name,
        output: rule.output.trim(),
        priority: segments.indexOf(e) + 1,
        matcher: rule.matcher,
      });
    });

    const rule = segmentAsJSON(segment, labelFilters);

    labelRules.push({
      active: true,
      createdAt: "2000-01-01T00:00:00Z",
      name: rule.name,
      output: rule.output.trim(),
      priority: segments.indexOf(segment) + 1,
      matcher: rule.matcher,
    });

    simulate({
      variables: {
        search: search ? search : undefined,
        labelRules: sortBy(labelRules, "priority"),
      },
    });
  }, [labelFilters, segments, segment, search, simulate]);

  const simulatedSegment = useMemo(
    () => ({ name: segment.name, priority: segments.indexOf(segment) + 1 }),
    [segments, segment],
  );

  const adsCount = useMemo(() => {
    return (
      data?.me.previewLabelRuleMatches.filter((row) => {
        const matchResult = row.matchResult;
        const firstMatch = matchResult.priorityOrderedMatchedRules[0];
        const isCurrentRule =
          firstMatch &&
          firstMatch.name === simulatedSegment.name &&
          firstMatch.priority === simulatedSegment.priority;

        return row.level === "ad" && isCurrentRule;
      }).length ?? 0
    );
  }, [data, simulatedSegment]);

  const adsetsCount = useMemo(() => {
    return (
      data?.me.previewLabelRuleMatches.filter((row) => {
        const matchResult = row.matchResult;
        const firstMatch = matchResult.priorityOrderedMatchedRules[0];
        const isCurrentRule =
          firstMatch &&
          firstMatch.name === simulatedSegment.name &&
          firstMatch.priority === simulatedSegment.priority;

        return row.level === "adset" && isCurrentRule;
      }).length ?? 0
    );
  }, [data, simulatedSegment]);

  const campaignsCount = useMemo(() => {
    return (
      data?.me.previewLabelRuleMatches.filter((row) => {
        const matchResult = row.matchResult;
        const firstMatch = matchResult.priorityOrderedMatchedRules[0];
        const isCurrentRule =
          firstMatch &&
          firstMatch.name === simulatedSegment.name &&
          firstMatch.priority === simulatedSegment.priority;

        return row.level === "campaign" && isCurrentRule;
      }).length ?? 0
    );
  }, [data, simulatedSegment]);

  const rows = useMemo(() => {
    if (!data) {
      return [];
    }

    const result = data.me.previewLabelRuleMatches
      .filter((row) => row.level === activeTab)
      .map((row) => {
        return {
          visitsLast30d: row.visitsLast30d,
          name: row.name,
          identificationMethod: row.identificationMethod,
          matchResult: row.matchResult,
          metadata: row.metadata,
        };
      });

    return orderBy(
      result,
      [
        sorting.column === "priorityOrderedMatchedRules"
          ? (row) => {
              const matchResult = row.matchResult;
              const firstMatch = matchResult.priorityOrderedMatchedRules[0];
              const isCurrentRule =
                firstMatch &&
                firstMatch.name === simulatedSegment.name &&
                firstMatch.priority === simulatedSegment.priority;

              return isCurrentRule ? 1 : 0;
            }
          : sorting.column,
        "visitsLast30d",
      ],
      [sorting.order, "desc"],
    );
  }, [data, sorting, simulatedSegment, activeTab]);

  const visibleRows = rows.slice(
    (page - 1) * PER_PAGE,
    (page - 1) * PER_PAGE + PER_PAGE,
  );

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <SearchInput
          placeholder="Search name..."
          value={search}
          onChange={(value) => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onChange",
              additionalActionInfo: "Search matches",
              data: {
                newSearch: value,
                pageContext: { search },
              },
            });

            setSearch(value);
          }}
        />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Tabs
            campaignsCount={campaignsCount}
            adsetsCount={adsetsCount}
            adsCount={adsCount}
            activeTab={activeTab}
            onActivate={(tabId: TabId) => {
              prettyLogEventPage({
                domain: "Breakdown v2",
                action: "onClick",
                additionalActionInfo: "Change preview matches tab",
                data: {
                  newTabId: tabId,
                  pageContext: { tabId },
                },
              });

              setPage(1);
              setActiveTab(tabId);
            }}
          />
          {rows.length > 0 && (
            <div className="mb-1">
              <Paginator
                page={page}
                perPage={PER_PAGE}
                total={rows.length}
                onChange={(value) => {
                  prettyLogEventPage({
                    domain: "Breakdown v2",
                    action: "onClick",
                    additionalActionInfo: "Change preview page",
                    data: {
                      newPage: value,
                      pageContext: { page },
                    },
                  });

                  setPage(value);
                }}
              />
            </div>
          )}
        </div>
        <div
          className={clsx(
            "border border-gray rounded-b-lg overflow-auto mt-[-1px] ",
            "min-h-[400px] max-h-[500px] overflow-auto",
          )}
        >
          {loading ? (
            <div className="h-[400px]">
              <LoadingSlide className="w-[400px]" />
            </div>
          ) : (
            <LabelRuleSimulatorTable
              rows={visibleRows}
              simulatedSegment={simulatedSegment}
              isDataPresent={!!data}
              loading={loading}
              error={error}
              sorting={sorting}
              onChangeSorting={(value) => {
                prettyLogEventPage({
                  domain: "Breakdown v2",
                  action: "onClick",
                  additionalActionInfo: "Change preview matches sorting",
                  data: {
                    newSorting: value,
                    pageContext: { sorting },
                  },
                });

                setSorting(value);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};
